import StencilHTMLElement from "./stencil-element";

class ProductSetComponent extends StencilHTMLElement {
  name = "ProductSetComponent";
  productChoices;
  productChoiceOptionsContainers;

  constructor() {
    super();
  }

  connectedCallback() {
    // Find parent product-set-picker element (assumed to always exist)
    this.parent = this.closest('product-set-picker');
    
    // Find all product choice inputs (radios that select which nested product)
    this.productChoices = this.querySelectorAll('[data-psc-product-id]');
    
    // Find all product choice options containers (contain variant pickers for each nested product)
    this.productChoiceOptionsContainers = this.querySelectorAll('.nested-choice-options[data-psc-product-id]');

    // Add change event listener to all product choice inputs
    this.productChoices.forEach(choice => {
      choice.addEventListener('change', this.handleProductChange.bind(this));
    });

    // Listen for clicks on swatches within product options containers
    this.addEventListener('click', this.handleProductOptionValueChange.bind(this));
    
    // Notify parent of initial selection
    this.notifyParentOfProductChange();
  }

  disconnectedCallback() {
    // Clean up event listeners
    this.productChoices?.forEach(choice => {
      choice.removeEventListener('change', this.handleProductChange);
    });
    this.removeEventListener('click', this.handleProductOptionValueChange);
  }

  /**
   * Handle product change events (when user selects a different nested product)
   * @param {Event} e 
   */
  handleProductChange(e) {
    this.updateProductOptionsVisibility();
    this.notifyParentOfProductChange();
  }

  /**
   * Handle product option value changes (swatch clicks)
   * 
   * TODO: More generic, not assuming swatches.
   * @param {Event} e 
   */
  handleProductOptionValueChange(e) {
    const target = e.target;
    
    // Only handle swatch clicks within visible product options containers
    const swatch = target.closest('.swatch');
    if (!swatch) {
      return;
    }
    
    // Get the variant-swatches container (sibling swatches)
    const swatchesContainer = swatch.closest('.variant-swatches');
    if (!swatchesContainer) {
      return;
    }
    
    // Remove active class from all swatches in this container
    swatchesContainer.querySelectorAll('.swatch').forEach(s => {
      s.classList.remove('active');
    });
    
    // Add active class to clicked swatch
    swatch.classList.add('active');
    
    // Notify parent of the change
    this.notifyParentOfProductOptionChange();
  }

  /**
   * Notify parent element of product change
   * Assumes at least one product choice is always checked (guaranteed by Liquid template)
   * Assumes parent element always exists
   */
  notifyParentOfProductChange() {
    const checkedChoice = this.querySelector('[data-psc-product-id]:checked');
    const choiceIndex = this.dataset.index;
    const productId = checkedChoice.value;
    const productHandle = checkedChoice.dataset.pscProductHandle;

    this.parent.dispatchEvent(new CustomEvent('product-set-component:product-changed', {
      detail: {
        choiceIndex,
        productId,
        productHandle
      },
      bubbles: true
    }));
  }

  /**
   * Get selected option value IDs for the currently selected product
   * @returns {Array<number>} Array of option value IDs
   */
  getSelectedOptionValueIds() {
    const checkedChoice = this.querySelector('[data-psc-product-id]:checked');
    if (!checkedChoice) {
      console.log(this.logPrefix, 'Failed to get selected option value IDs: no checked choice');
      return [];
    }

    const productId = checkedChoice.value;
    const optionsContainer = this.querySelector(
      `.nested-choice-options:not(.d-none)[data-psc-product-id="${productId}"]`
    );
    
    if (!optionsContainer) {
      console.log(this.logPrefix, 'Failed to get selected option value IDs: no options container');
      return [];
    }

    let optionValueIds = [];

    // Active swatches or other active elements
    optionsContainer.querySelectorAll('.active[data-option-value-id]').forEach(element => {
      if (element.dataset.optionValueId) optionValueIds.push(parseInt(element.dataset.optionValueId));
    });

    // Checked radios with data-option-value-id (buttons)
    optionsContainer.querySelectorAll('input[type="radio"]:checked[data-option-value-id]').forEach(radio => {
      optionValueIds.push(parseInt(radio.dataset.optionValueId));
    });

    optionValueIds = [...new Set(optionValueIds.filter(value => Number.isFinite(value)))];
    console.log(this.logPrefix, 'Selected option value IDs:', optionValueIds);

    return optionValueIds;
  }

  /**
   * Notify parent element of product option change
   * Assumes parent element always exists
   */
  notifyParentOfProductOptionChange() {
    const choiceIndex = this.dataset.index;
    const optionValueIds = this.getSelectedOptionValueIds();

    this.parent.dispatchEvent(new CustomEvent('product-set-component:product-option-changed', {
      detail: {
        choiceIndex,
        optionValueIds
      },
      bubbles: true
    }));
  }

  /**
   * Update visibility of product choice options containers based on selected product choice
   * Assumes at least one product choice is always checked (guaranteed by Liquid template)
   */
  updateProductOptionsVisibility() {
    // Find the checked product choice input
    const checkedChoice = this.querySelector('[data-psc-product-id]:checked');
    
    // Get the value of the checked choice (product ID)
    const productId = checkedChoice.value;

    // Hide all product choice options containers
    this.productChoiceOptionsContainers.forEach(container => {
      container.classList.add('d-none');
    });

    // Show the container that matches the selected product ID
    const matchingContainer = this.querySelector(`.nested-choice-options[data-psc-product-id="${productId}"]`);
    if (matchingContainer) {
      matchingContainer.classList.remove('d-none');
    }
  }
}

export default ProductSetComponent;
